import _ from "lodash";
import { useEffect, useMemo, useState, useCallback } from "react";

import { Plus } from "lucide-react";
import { useDispatch } from "react-redux";

import { toast } from "@/design-system/components/Toast/Toast";

import { ToggledEditableBlock } from "@/design-system/components/ToggledEditableBlock/ToggledEditableBlock";
import { Button } from "@/design-system/components/Button/Button";
import { useSetBreadcrumbs } from "@/NavBar/NavigationLayoutContext";
import { makeRequest } from "../util";
import useAccountMetadata from "../hooks/useAccountMetadata";
import useUsers from "../hooks/useUsers";
import FreshpaintUser from "../components/users/FreshpaintUser";
import { User, Invite } from "../components/users/types";
import { fetchUsers } from "../actions/users";
import DeleteInviteModal from "./modals/DeleteInviteModal";
import InviteUserModal from "./modals/InviteUserModal";
import { DeleteUserModal } from "./modals/DeleteUserModal";
import { ChangeUserRoleModal } from "./modals/ChangeUserRoleModal";
import { FormAccessManagementModal } from "./modals/FormAccessManagementModal";
import { EnvironmentAccessModal } from "./modals/EnvironmentAccessModal";
import { MemberList } from "./lists/MemberList";
import { InvitesList } from "./lists/InvitesList";

import { updateCompanyDetails } from "@/account/api/company";
import { getCurrentUser } from "../auth/firebase";
import {
  allEnvironmentIDs,
  inviteUser,
  RoleAdmin,
  RoleInviteDefault,
} from "@/components/users/inviteUser";

type UserEnvsMap = Record<string, Record<string, Record<string, boolean>>>;

function TeamPage() {
  const dispatch = useDispatch();

  const accountMetadata = useAccountMetadata();
  const usersState = useUsers();

  const [inviteUserEmail, setInviteUserEmail] = useState<string | null>(null);
  const [inviteUserEmailHasError, setInviteUserEmailHasError] = useState(false);
  const [inviteUserRole, setInviteUserRole] = useState(RoleInviteDefault);
  const [inviteSelectedEnvs, setInviteSelectedEnvs] = useState<Record<
    string,
    boolean
  > | null>(null);

  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToChangeRole, setUserToChangeRole] = useState<User | null>(null);
  const [userToManageForms, setUserToManageForms] = useState<User | null>(null);
  const [userToManageEnvs, setUserToManageEnvs] = useState<User | null>(null);

  const [inviteToDelete, setInviteToDelete] = useState<Invite | null>(null);

  const invites = useMemo(() => {
    if (usersState.loading) {
      return [];
    }
    return _(usersState.invitedUsers)
      .sortBy((invite) => invite.to_email)
      .value();
  }, [usersState]);

  const members = useMemo(() => {
    if (usersState.loading) {
      return null;
    }

    const currentUserEmail = getCurrentUser()?.email;

    const you = usersState.users.filter(
      (user) => user.email === currentUserEmail,
    );
    const otherUsers = _(usersState.users)
      .filter((user) => user.email !== currentUserEmail)
      .sortBy((user) => user.email)
      .value();

    return you.concat(otherUsers);
  }, [usersState]);

  const currentMember = useMemo(() => {
    let you;
    if (!usersState.loading) {
      const authUser = getCurrentUser();
      you = usersState.users.find((member) => member.uid === authUser?.uid);
    }
    return new FreshpaintUser(you);
  }, [usersState]);

  const [loadingUserEnvs, setLoadingUserEnvs] = useState(true);
  const [userEnvs, setUserEnvs] = useState<UserEnvsMap | null>(null);

  const canEditCompanyInfo = useMemo(
    () => currentMember.hasAdminAccess(),
    [currentMember],
  );

  function fetchUserEnvs() {
    makeRequest("get_all_non_admin_envs")
      .then((response) => {
        const envUsersList = response.data as Array<{
          uid: string;
          proj_id: string;
          env_id: string;
        }>;
        const out: UserEnvsMap = {};
        for (const { uid, proj_id, env_id } of envUsersList) {
          const projMap = out[uid] || {};
          const envMap = projMap[proj_id] || {};
          envMap[env_id] = true;
          projMap[proj_id] = envMap;
          out[uid] = projMap;
        }
        setUserEnvs(out);
        setLoadingUserEnvs(false);
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  useEffect(() => {
    fetchUserEnvs();
  }, []);

  const handleCompanyNameUpdate = useCallback(async (v) => {
    try {
      await updateCompanyDetails({
        newName: v,
      });
    } catch (error) {
      toast.error("Something went wrong. Unable to update company name.");
      console.error(error);
    }
  }, []);

  function handleUserEnvAccessUpdate(uid, projectID, envID, hasAccess) {
    makeRequest("update_user_env", {
      uid,
      proj_id: projectID,
      env_id: envID,
      has_access: hasAccess,
    })
      .then(() => {
        fetchUserEnvs();
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleInviteUser() {
    if (inviteUserEmailHasError) {
      toast.error("You must provide a valid email to send invite");
      return;
    }

    if (!inviteUserEmail) {
      toast.error("You must provide an email to send invite");
      return;
    }

    if (!inviteUserRole) {
      toast.error("You must provide a role to send invite");
      return;
    }

    let envIDs;
    if (inviteUserRole === RoleAdmin) {
      envIDs = [];
    } else if (!inviteSelectedEnvs) {
      // No explicit selection means "grant access to every environment".
      envIDs = allEnvironmentIDs(accountMetadata);
    } else {
      envIDs = _(inviteSelectedEnvs)
        .entries()
        .filter((e) => e[1])
        .map((e) => e[0])
        .value();
    }

    inviteUser({ email: inviteUserEmail, role: inviteUserRole, envIDs })
      .then(() => {
        setInviteUserEmail(null);
        setInviteUserEmailHasError(false);
        setInviteSelectedEnvs(null);
        setInviteUserRole(RoleInviteDefault);
        dispatch(fetchUsers());
        toast.success("Invite Sent");
      })
      .catch((error) => {
        if (error.response && error.response.status === 429) {
          toast.error("Too many requests, try again later.");
          return;
        }
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleDeleteInvite() {
    if (!inviteToDelete) return;
    makeRequest("remove_user_invite", { invite_id: inviteToDelete.id })
      .then(() => {
        dispatch(fetchUsers());
        setInviteToDelete(null);
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to remove invite.");
        console.error(error);
      });
  }

  function handleDeleteUser() {
    if (!userToDelete) return;
    makeRequest("remove_user", { uid: userToDelete.uid })
      .then(() => {
        dispatch(fetchUsers());
        setUserToDelete(null);
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleRoleChange(selectedMember, newRole) {
    if (selectedMember.role === newRole) {
      return;
    }

    makeRequest("update_user_role", {
      uid: selectedMember.uid,
      role: newRole,
    })
      .then(() => {
        setUserToChangeRole(null);
        dispatch(fetchUsers());
      })
      .catch((error) => {
        if (error.response.status === 403) {
          toast.error(
            "You're not authorized to perform this action. Please reload page.",
          );
          return;
        }
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  function handleFormSubmissionAccessSave(selectedMember, formIDs) {
    makeRequest("save_form_submission_access", {
      uid: selectedMember.uid,
      form_ids: formIDs,
    })
      .then(() => {
        toast.success("Form submission access saved");
      })
      .catch((error) => {
        if (error.response.status === 403) {
          toast.error(
            "You're not authorized to perform this action. Please reload page.",
          );
          return;
        }
        toast.error("Something went wrong. Unable to load page.");
        console.error(error);
      });
  }

  return (
    <div className="max-w-8xl m-auto space-y-8">
      <div className="flex justify-between">
        <div className="flex flex-col gap-4">
          <h1>Team members</h1>
          <p className="text-textSecondary max-w-5xl">
            Invite teammates and manage their roles. Roles govern what each
            member can view and change across the platform; non-admin roles can
            be scoped to specific environments.
          </p>
        </div>
        <Button
          disabled={!currentMember.hasAdminAccess()}
          tooltipContent={
            !currentMember.hasAdminAccess()
              ? "You're not authorized to perform this action."
              : undefined
          }
          leadingIcon={<Plus />}
          onClick={() => {
            setInviteUserEmail("");
            setInviteUserEmailHasError(false);
            setInviteUserRole(RoleInviteDefault);
          }}
        >
          Add teammate
        </Button>
      </div>
      <h2>
        {canEditCompanyInfo ? (
          <ToggledEditableBlock onNewValue={handleCompanyNameUpdate}>
            {accountMetadata.companyDetails.name || "Company details"}
          </ToggledEditableBlock>
        ) : (
          accountMetadata.companyDetails.name || "Company details"
        )}
      </h2>
      {invites.length > 0 && (
        <InvitesList
          currentMember={currentMember}
          invites={invites}
          onClickDelete={setInviteToDelete}
        />
      )}
      {members && (
        <MemberList
          currentMember={currentMember}
          members={members}
          onClickDelete={setUserToDelete}
          onClickChangeRole={setUserToChangeRole}
          onClickFormsAccess={setUserToManageForms}
          onClickEnvAccess={setUserToManageEnvs}
        />
      )}

      <InviteUserModal
        open={inviteUserEmail !== null}
        onClose={() => {
          setInviteUserEmail(null);
          setInviteUserEmailHasError(false);
          setInviteUserRole(RoleInviteDefault);
        }}
        onInvite={handleInviteUser}
        onEmailChange={setInviteUserEmail}
        inviteUserRole={inviteUserRole}
        onRoleChange={(newRole) => {
          setInviteUserRole(newRole);
        }}
        inviteSelectedEnvs={inviteSelectedEnvs}
        setInviteSelectedEnvs={setInviteSelectedEnvs}
        setHasEmailError={setInviteUserEmailHasError}
      />
      <ChangeUserRoleModal
        open={userToChangeRole !== null}
        onClose={() => {
          setUserToChangeRole(null);
        }}
        onRoleChange={(newRole) => {
          handleRoleChange(userToChangeRole, newRole);
        }}
        defaultRole={userToChangeRole?.role}
        member={userToChangeRole}
      />
      <FormAccessManagementModal
        open={userToManageForms !== null}
        onClose={() => {
          setUserToManageForms(null);
        }}
        onSave={(formIDs) => {
          handleFormSubmissionAccessSave(userToManageForms, formIDs);
        }}
        member={userToManageForms}
      />
      <EnvironmentAccessModal
        open={userToManageEnvs !== null}
        member={userToManageEnvs}
        onClose={() => setUserToManageEnvs(null)}
        loadingUserEnvs={loadingUserEnvs}
        userEnvs={userEnvs}
        enableToggle={currentMember.hasAdminAccess()}
        onUserEnvAccessUpdate={handleUserEnvAccessUpdate}
      />
      <DeleteUserModal
        open={userToDelete !== null}
        user={userToDelete}
        onClose={() => setUserToDelete(null)}
        onDelete={handleDeleteUser}
      />
      <DeleteInviteModal
        open={inviteToDelete !== null}
        invite={inviteToDelete}
        onClose={() => setInviteToDelete(null)}
        onDelete={handleDeleteInvite}
      />
    </div>
  );
}

const breadcrumbs = [
  {
    label: "Settings",
  },
  {
    label: "Team members",
    href: "/settings/team",
  },
];

function TeamPageWithNavigation() {
  useSetBreadcrumbs(breadcrumbs);

  return <TeamPage />;
}

export default TeamPageWithNavigation;
